import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { LibraryItemDetail, MediaInfo } from '@shared/types.ts';
import { api } from '@/lib/api.ts';
import { useMe } from '@/lib/queries.ts';
import { Focusable } from '@/ui/Focusable.tsx';
import { Modal } from '@/ui/Modal.tsx';
import { usePlayItem } from './usePlayItem.ts';

/**
 * Everything Plex offers on an item that is not "play it".
 *
 * THE PRINCIPLE: this app exists to be better than the one it replaces, so
 * anything Plex puts behind a long-press on a poster has to be here too.
 * Continue Watching with no way to remove something from it is the example
 * that made the point — a film somebody bounced off twenty minutes in sits at
 * the front of every home screen forever.
 *
 * Every action changes PLEX'S state, as that viewer, so it shows up in Plex's
 * own apps as well. N8Media keeps no watched flags of its own: a second copy
 * is how two systems end up disagreeing about whether you have seen an
 * episode.
 *
 * MARKING WATCHED AND REMOVING FROM CONTINUE WATCHING ARE DIFFERENT THINGS,
 * and both are offered because the difference is the whole point. Marking a
 * half-watched film "watched" to clear it off the home screen is a lie that
 * then hides it from every unwatched filter too.
 */
export function ItemMenu({ item, onClose }: { item: LibraryItemDetail; onClose(): void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { play } = usePlayItem();
  const { data: me } = useMe();
  const [busy, setBusy] = useState<string | null>(null);
  /* Two presses, and it disarms itself. An armed delete left sitting on
     screen is a landmine for whoever walks past in four minutes. */
  const [armed, setArmed] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);

  const go = (id: string) => { onClose(); navigate(`/library/item/${encodeURIComponent(id)}`); };

  const watched = item.userData.played;
  const started = item.userData.positionMs > 0;

  const act = (action: string) => {
    setBusy(action);
    void api.itemAction(item.id, action)
      .then(() => {
        /*
         * Everything the library screens show comes from Plex, so the honest
         * refresh is to ask again rather than to patch a local copy — which
         * would be a third place the watched state lives.
         */
        void qc.invalidateQueries({ queryKey: ['library'] });
        onClose();
      })
      .catch(() => setBusy(null));
  };

  return (
    <Modal className="menu-dialog" onClose={onClose} labelledBy="item-menu-title">
      <div className="menu-head">
        <div>
          <div className="big" id="item-menu-title">{item.title}</div>
          {item.year && <div className="muted">{item.year}</div>}
        </div>
      </div>

      <div className="menu-items">
        {/* First, because it is the one thing here somebody might be in a
            hurry to do. Resuming is what pressing the poster already does, so
            this is the other half of that choice. */}
        {started && (
          <Focusable
            id="item-restart"
            className="btn menu-item"
            onSelect={() => {
              setBusy('reset-progress');
              void api.itemAction(item.id, 'reset-progress')
                .then(() => { onClose(); void play({ ...item, userData: { ...item.userData, positionMs: 0 } }); })
                .catch(() => setBusy(null));
            }}
          >
            Play from the beginning
          </Focusable>
        )}

        {started && (
          <Focusable
            id="item-remove-continue"
            className="btn menu-item"
            onSelect={() => act('remove-from-continue')}
          >
            Remove from Continue Watching
            <span className="menu-note-inline">Keeps it unwatched</span>
          </Focusable>
        )}

        <Focusable
          id="item-watched"
          className="btn menu-item"
          onSelect={() => act(watched ? 'unwatched' : 'watched')}
        >
          {watched ? 'Mark as unwatched' : 'Mark as watched'}
        </Focusable>

        {/*
          UP THE HIERARCHY, which an episode had no way to offer.
          
          Landing on an episode — from Continue Watching, from search, from a
          New Episodes tile — left you with no route to the series it belongs
          to except going back to the library and finding it again. Plex puts
          these behind the same long-press, and the ids have been on the item
          all along.
        */}
        {item.grandparentId && (
          <Focusable
            id="item-goto-show"
            className="btn menu-item"
            onSelect={() => go(item.grandparentId!)}
          >
            Go to show
          </Focusable>
        )}

        {item.parentId && item.type === 'episode' && (
          <Focusable
            id="item-goto-season"
            className="btn menu-item"
            onSelect={() => go(item.parentId!)}
          >
            {item.parentIndex ? `Go to season ${item.parentIndex}` : 'Go to season'}
          </Focusable>
        )}
      </div>

      {/* WHAT THE FILE IS. Read-only, and the answer to "why is this one
          transcoding" as well as "does this have Spanish audio". Plex has had
          it all along; the detail response already carries it. */}
      {item.media && <FileInfo media={item.media} />}

      {/*
        DELETING IS AN ADMIN'S, and the server agrees — it answers 404 to
        anybody else rather than 403, because a permission error confirms the
        thing exists. Hidden here as well so nobody presses a button that was
        only ever going to refuse.
      */}
      {me?.user.can.admin && (
        <div className="menu-items">
          <Focusable
            id="item-delete"
            className={`btn menu-item${armed ? ' menu-item-danger' : ''}`}
            onSelect={() => {
              if (!armed) { setArmed(true); return; }
              setArmed(false);
              setBusy('delete');
              setFailed(null);
              void api.deleteItem(item.id)
                .then(() => {
                  void qc.invalidateQueries({ queryKey: ['library'] });
                  onClose();
                })
                .catch((e) => { setBusy(null); setFailed((e as Error).message); });
            }}
          >
            {armed ? 'Delete permanently — press again' : 'Delete'}
          </Focusable>
        </div>
      )}

      {failed && <div className="menu-note account-warn">{failed}</div>}
      {busy && <div className="menu-note muted">Working…</div>}

      {/*
        CANCEL AT THE BOTTOM. It was at the top once, which put the one thing
        that does nothing first in the cursor's path on every single open.
      */}
      <Focusable id="item-menu-close" className="btn menu-item" onSelect={onClose}>
        Cancel
      </Focusable>
    </Modal>
  );
}

/**
 * The file, in words.
 *
 * READ-ONLY ON PURPOSE. The languages are listed because the server knows
 * them and "does this have Spanish audio" is a real question — but choosing
 * one is the player's job, so this says what is IN the file rather than
 * offering to change it.
 *
 * It is also the honest answer to "why did this one transcode": a browser
 * refuses mkv, hevc and ac-3, and those three words being visible here is the
 * difference between a mystery and a fact.
 */
function FileInfo({ media }: { media: MediaInfo }) {
  const video = [media.resolution, media.videoCodec, media.container]
    .filter(Boolean).join(' · ');
  const audio = [media.audioCodec, channels(media.audioChannels)]
    .filter(Boolean).join(' · ');

  const rows: Array<[string, string]> = [];
  if (video) rows.push(['Video', video]);
  if (audio) rows.push(['Audio', audio]);
  if (media.audioLanguages.length) rows.push(['Audio tracks', media.audioLanguages.join(', ')]);
  if (media.subtitleLanguages.length) rows.push(['Subtitles', media.subtitleLanguages.join(', ')]);
  if (media.sizeBytes) rows.push(['Size', gigabytes(media.sizeBytes)]);

  return (
    <div className="file-info">
      <div className="file-info-head muted">File</div>
      {rows.length === 0
        /* Not an error, and worth saying plainly — an empty panel reads as
           something broken rather than as a library that has not been scanned. */
        ? <div className="muted file-info-empty">Plex has not analysed this file.</div>
        : rows.map(([label, value]) => (
          <div key={label} className="file-info-row">
            <span className="muted file-info-label">{label}</span>
            <span className="file-info-value">{value}</span>
          </div>
        ))}
    </div>
  );
}

/** How people say it, rather than a channel count nobody pictures. */
function channels(n: number | null): string | null {
  if (!n) return null;
  return n === 1 ? 'Mono' : n === 2 ? 'Stereo' : `${n} channels`;
}

function gigabytes(bytes: number): string {
  const gb = bytes / 1024 / 1024 / 1024;
  return gb >= 1 ? `${gb.toFixed(1)} GB` : `${Math.round(bytes / 1024 / 1024)} MB`;
}
